import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { DailyCallService } from '@services/dailycall';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { useRecordingContext } from '../../../context/RecordingContext';
import { useTelehealthCallManager } from '../context/useTelehealthCallManager';
import { setChatPanelOpen } from '../store';
import type { TelehealthSession } from '../types';

import { CompactPipView } from './pip/CompactPipView';
import { PipView } from './pip/PipView';
import { FloatingTelehealthPanelShell } from './FloatingTelehealthPanelShell';

interface CallPanelProps {
  session: TelehealthSession;
  activeEncounterId: string;
  dailyCallService: DailyCallService;
  isRecording: boolean;
  isRecordingStarting?: boolean;
  isRecordingPaused?: boolean;
  onLeaveCall: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
}

export function CallPanel({
  session,
  activeEncounterId,
  dailyCallService,
  isRecording,
  isRecordingStarting,
  isRecordingPaused,
  onLeaveCall,
  onPauseRecording,
  onResumeRecording,
}: CallPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { telehealth } = useEventTracking();
  const dispatch = useAppDispatch();

  const { audioInputDeviceService, setSelectedMicrophone } = useRecordingContext();
  const { pip } = useTelehealthCallManager();

  const isOnActiveEncounter = location.pathname.includes(`/encounters/${activeEncounterId}`);

  const handleReturnToEncounter = useCallback(() => {
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, navigate]);

  const handleOpenChat = useCallback(() => {
    dispatch(setChatPanelOpen(true));
    navigate(`/encounters/${activeEncounterId}`);
  }, [activeEncounterId, dispatch, navigate]);

  // Leave always stops recording first (handled by TelehealthCallManager.handleLeaveInternal)
  const handleLeaveClick = useCallback(() => {
    telehealth.callLeft({
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId ?? '',
    });
    onLeaveCall();
  }, [onLeaveCall, session, telehealth]);

  return (
    <FloatingTelehealthPanelShell>
      {({ isCompact, compactControls, fullscreen, panelRef }) => (
        <>
          {isCompact ? (
            <CompactPipView
              dailyCallService={dailyCallService}
              isRecording={isRecording}
              isRecordingStarting={isRecordingStarting}
              isRecordingPaused={isRecordingPaused}
              onLeave={handleLeaveClick}
              onClose={isOnActiveEncounter ? undefined : handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
              onOpenChat={handleOpenChat}
              onPauseRecording={onPauseRecording}
              onResumeRecording={onResumeRecording}
              onToggleFullscreen={fullscreen.isSupported ? fullscreen.toggle : undefined}
              isFullscreen={fullscreen.isFullscreen}
            />
          ) : (
            <PipView
              dailyCallService={dailyCallService}
              audioInputDeviceService={audioInputDeviceService}
              setSelectedMicrophone={setSelectedMicrophone}
              isRecording={isRecording}
              isRecordingStarting={isRecordingStarting}
              isRecordingPaused={isRecordingPaused}
              session={session}
              onLeave={handleLeaveClick}
              onClose={isOnActiveEncounter ? undefined : handleReturnToEncounter}
              onEnterPip={pip.toggle}
              isPipSupported={pip.isSupported}
              onPauseRecording={onPauseRecording}
              onResumeRecording={onResumeRecording}
              onToggleFullscreen={fullscreen.isSupported ? fullscreen.toggle : undefined}
              isFullscreen={fullscreen.isFullscreen}
              compactControls={compactControls}
              popoverContainerEl={fullscreen.isFullscreen ? panelRef.current : null}
            />
          )}
        </>
      )}
    </FloatingTelehealthPanelShell>
  );
}
